import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './SmsLogs.css';

const sourceOf = (reference = '') => {
    if (reference.startsWith('career_')) return 'Job application';
    if (reference.startsWith('comm_')) return 'Messages';
    if (reference.startsWith('invite_')) return 'Invitation';
    return 'System';
};

const SmsLogs = () => {
    const { t } = useLanguage();
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({ sent: 0, failed: 0 });
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
    const [filters, setFilters] = useState({ status: '', search: '' });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, per_page: 50 };
            if (filters.status) params.status = filters.status;
            if (filters.search.trim()) params.search = filters.search.trim();
            const res = await api.getSmsLogs(params);
            setLogs(res.data || []);
            setSummary(res.summary || { sent: 0, failed: 0 });
            setPagination(res.pagination || { page: 1, total_pages: 1, total: 0 });
        } catch (err) {
            console.error('Failed to load SMS logs', err);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => { load(); }, [load]);

    const formatDateTime = (value) => value ? new Date(value.replace(' ', 'T')).toLocaleString('sw-TZ', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

    const clearLogs = async () => {
        if (!window.confirm(t('Delete ALL SMS records? This cannot be undone.'))) return;
        try {
            const res = await api.clearSmsLogs();
            alert(`${res.deleted ?? 0} ${t('SMS records deleted.')}`);
            setPage(1);
            load();
        } catch (err) {
            alert(err.message);
        }
    };

    const onSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setFilters({ ...filters, search: e.target.elements.search.value });
    };

    return (
        <div className="sms-logs-page">
            <div className="sms-logs-header">
                <div>
                    <h1>{t('SMS Logs')}</h1>
                    <p>{t('Every SMS sent by the system and whether the gateway accepted it')}</p>
                </div>
                <div className="sms-logs-actions">
                    <button className="btn btn-secondary" onClick={load}>{t('Refresh')}</button>
                    <button className="btn btn-danger" disabled={!pagination.total} onClick={clearLogs}>{t('Clear SMS logs')}</button>
                </div>
            </div>

            <div className="sms-logs-summary">
                <div><span>{t('Sent (30 days)')}</span><b>{summary.sent || 0}</b></div>
                <div className="failed"><span>{t('Failed (30 days)')}</span><b>{summary.failed || 0}</b></div>
            </div>

            <div className="sms-logs-toolbar">
                <form onSubmit={onSearch}>
                    <input name="search" type="search" defaultValue={filters.search} placeholder={t('Search phone, message or reference')} />
                    <button type="submit" className="btn btn-primary">{t('Search')}</button>
                </form>
                <select value={filters.status} onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value }); }}>
                    <option value="">{t('All statuses')}</option>
                    <option value="sent">{t('Sent')}</option>
                    <option value="failed">{t('Failed')}</option>
                </select>
            </div>

            <div className="sms-logs-table">
                {loading ? (
                    <p className="sms-logs-empty">{t('Loading...')}</p>
                ) : logs.length === 0 ? (
                    <p className="sms-logs-empty">{t('No SMS records yet.')}</p>
                ) : (
                    <div className="table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('Date')}</th>
                                    <th>{t('Phone')}</th>
                                    <th>{t('Source')}</th>
                                    <th>{t('Message')}</th>
                                    <th>{t('Status')}</th>
                                    <th>{t('Gateway response')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.id}>
                                        <td>{formatDateTime(log.created_at)}</td>
                                        <td>{log.recipient}</td>
                                        <td>{t(sourceOf(log.reference || ''))}</td>
                                        <td className="sms-message" onClick={() => setExpanded(expanded === log.id ? null : log.id)} title={log.message}>
                                            {expanded === log.id ? log.message : (log.message.length > 60 ? `${log.message.slice(0, 60)}…` : log.message)}
                                        </td>
                                        <td><span className={`sms-status ${log.status}`}>{t(log.status === 'sent' ? 'Sent' : 'Failed')}</span></td>
                                        <td className="sms-provider">{log.provider_status || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {pagination.total_pages > 1 && (
                <div className="sms-logs-pager">
                    <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ {t('Previous')}</button>
                    <span>{page} / {pagination.total_pages}</span>
                    <button className="btn btn-secondary" disabled={page >= pagination.total_pages} onClick={() => setPage(page + 1)}>{t('Next')} ›</button>
                </div>
            )}
        </div>
    );
};

export default SmsLogs;
